import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import Login from './components/Login/Login';
import Layout from './components/Layout/Layout';
import AutomationProperties from './components/AutomationProperties/AutomationProperties';
import Dashboard from './components/Dashboard/Dashboard';
import AppLoginActivity from './components/AppLoginActivity/AppLoginActivity';
import RemoteServer from './components/RemoteServer/RemoteServer';
import UserManagement from './components/UserManagement/UserManagement';
import Settings from './components/Settings/Settings';
import ApiDocs from './components/ApiDocs/ApiDocs';
import './App.css';

// Theme Context
type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  cycleTheme: () => void;
  resolvedTheme: 'light' | 'dark';
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'system',
  setTheme: () => {},
  cycleTheme: () => {},
  resolvedTheme: 'light',
});

export const useTheme = () => useContext(ThemeContext);

// Admin Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  
  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

// Helper to get system preference
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

// Helper to validate and migrate theme from localStorage
const getValidTheme = (savedTheme: string | null): Theme => {
  if (savedTheme === 'light' || savedTheme === 'dark' || savedTheme === 'system') {
    return savedTheme;
  }
  // Migrate old 'default' value or any invalid value to 'system'
  return 'system';
};

function App() {
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('cgr-theme');
    return getValidTheme(savedTheme);
  });

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    if (theme === 'system') {
      return getSystemTheme();
    }
    return theme as 'light' | 'dark';
  });

  // Apply theme to document
  const applyTheme = useCallback((themeToApply: 'light' | 'dark') => {
    document.documentElement.setAttribute('data-theme', themeToApply);
    setResolvedTheme(themeToApply);
  }, []);

  // Handle theme changes
  useEffect(() => {
    localStorage.setItem('cgr-theme', theme);
    
    if (theme === 'system') {
      applyTheme(getSystemTheme());
    } else {
      applyTheme(theme);
    }
  }, [theme, applyTheme]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      applyTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, applyTheme]);

  const cycleTheme = () => {
    const themes: Theme[] = ['system', 'light', 'dark'];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, cycleTheme, resolvedTheme }}>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Public Route - Login */}
              <Route path="/login" element={<Login />} />
              
              {/* Protected Routes */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Layout />
                  </PrivateRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="automation" element={<AutomationProperties />} />
                <Route path="app-login-activity" element={<AppLoginActivity />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="remote-server" element={<RemoteServer />} />
                <Route path="settings" element={<Settings />} />
                
                {/* API Documentation - Available for all logged-in users */}
                <Route path="api-docs" element={<ApiDocs />} />
                
                {/* User Management - Admin Only */}
                <Route 
                  path="users" 
                  element={
                    <AdminRoute>
                      <UserManagement />
                    </AdminRoute>
                  } 
                />
              </Route>
              
              {/* Catch all - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            
            {/* Toast notifications */}
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme={resolvedTheme}
            />
          </div>
        </Router>
      </AuthProvider>
    </ThemeContext.Provider>
  );
}

// Reports page component
const ReportsPage = () => (
  <div className="page-container">
    <h1>Reports</h1>
    <p>View automation reports and analytics</p>
    <div className="reports-content">
      <h3>Available Reports:</h3>
      <ul className="reports-list">
        <li>Daily automation summary</li>
        <li>Property status overview</li>
        <li>Application performance metrics</li>
        <li>Success/failure analytics</li>
      </ul>
    </div>
  </div>
);

export default App;
export { AdminRoute };
